/**
 * Standard header for match-flow node player views.
 *
 * Renders an animated centered title + subtitle block matching the
 * map-vote standard typography. The `accent` prop tints the title color
 * for nodes that lean into a danger/warning mood (e.g., character-ban).
 */

import { motion } from "framer-motion";
import type { ReactNode } from "react";
import { cn } from "../../lib/utils";

type NodeHeaderAccent = "brand" | "red" | "amber";

const ACCENT_TEXT: Record<NodeHeaderAccent, string> = {
  brand: "text-brand-400",
  red: "text-red-400",
  amber: "text-amber-400",
};

type NodeHeaderProps = {
  title?: string;
  subtitle?: ReactNode;
  accent?: NodeHeaderAccent;
};

export function NodeHeader({ title, subtitle, accent = "brand" }: NodeHeaderProps) {
  if (!title && !subtitle) return null;

  return (
    <motion.div
      initial={{ opacity: 0, y: -10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4 }}
      className="text-center mb-6"
    >
      {title && (
        <h3
          className={cn(
            "font-display text-sm @md:text-base font-bold uppercase tracking-[0.3em] mb-1",
            ACCENT_TEXT[accent],
          )}
        >
          {title}
        </h3>
      )}
      {subtitle &&
        (typeof subtitle === "string" ? (
          <p className="text-text-secondary text-sm">{subtitle}</p>
        ) : (
          subtitle
        ))}
    </motion.div>
  );
}
